import { apiClient } from './client';

export type PreferenceNamespace = 'ui' | 'notifications';
export type PreferenceScope = 'global' | 'organization';

export interface UiPreferences {
  theme: 'system' | 'light' | 'dark';
  language: 'fr' | 'en' | 'ar';
  timezone: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  pageSize: number;
  defaultLandingPage: string;
}

export interface NotificationPreferences {
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

export interface ResolvedPreferences {
  context: { userId: string; organizationId: string | null };
  preferences: { ui: UiPreferences; notifications: NotificationPreferences };
}

export interface ResolvedNamespace<T> {
  context: { userId: string; organizationId: string | null };
  namespace: PreferenceNamespace;
  values: T;
  sources: { globalVersion: number; organizationUserVersion: number; organizationDefaultVersion: number };
}

const versionHeader = (version: number) => ({ 'If-Match': `"${version}"` });

export const preferencesApi = {
  getAll(): Promise<ResolvedPreferences> {
    return apiClient.get<ResolvedPreferences>('/v1/me/preferences').then((response) => response.data);
  },
  getNamespace<T>(namespace: PreferenceNamespace): Promise<ResolvedNamespace<T>> {
    return apiClient.get<ResolvedNamespace<T>>(`/v1/me/preferences/${namespace}`).then((response) => response.data);
  },
  update<T extends object>(namespace: PreferenceNamespace, scope: PreferenceScope, values: Partial<T>, version: number): Promise<unknown> {
    return apiClient.patch(`/v1/me/preferences/${namespace}`, values, { params: { scope }, headers: versionHeader(version) }).then((response) => response.data);
  },
  reset(namespace: PreferenceNamespace, scope: PreferenceScope, version: number): Promise<unknown> {
    return apiClient.delete(`/v1/me/preferences/${namespace}`, { params: { scope }, headers: versionHeader(version) }).then((response) => response.data);
  },
};

export const THEME_RENDER_CACHE_KEY = 'nwm.last-resolved-theme';

export function applyResolvedTheme(theme: UiPreferences['theme']): void {
  const effective = theme === 'system'
    ? (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  document.documentElement.dataset.theme = effective;
  document.documentElement.style.colorScheme = effective;
  try { window.localStorage.setItem(THEME_RENDER_CACHE_KEY, theme); } catch { /* rendering cache is optional */ }
}
